/**
 * User API Client
 * Handles user sync with the backend after Stytch authentication.
 */

import type { UserDto, UserResponse } from "@shared/contracts/api";
import { ApiError, apiRequest } from "./api/client";

export type UserInfo = UserDto;

/**
 * Sync the Stytch user to the database.
 * Should be called immediately after login/signup
 */
export async function syncUser(authToken: string): Promise<UserInfo> {
  try {
    const data = await apiRequest<UserResponse>("/api/user/sync", {
      method: "POST",
      authToken,
    });
    if (!data.user) throw new Error("Failed to sync user to database");
    return data.user;
  } catch (error) {
    if (error instanceof ApiError) {
      console.error(
        "[UserAPI] Failed to sync user:",
        error.status,
        error.details,
      );
    } else {
      console.error("[UserAPI] Error syncing user:", error);
    }
    throw error;
  }
}

/**
 * Check if user exists in database
 */
export async function getUser(): Promise<{ user: UserInfo | null; exists: boolean }> {
  try {
    const data = await apiRequest<UserResponse>("/api/user");
    return { user: data.user, exists: data.exists ?? false };
  } catch (error) {
    if (error instanceof ApiError) {
      console.error(
        "[UserAPI] Failed to get user:",
        error.status,
        error.details,
      );
    } else {
      console.error("[UserAPI] Error getting user:", error);
    }
    return { user: null, exists: false };
  }
}
